import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { icuAPI, patientAPI } from '../../utils/api';
import api from '../../utils/api';

const VENT_STATUS = ['None', 'Intubated', 'Non-Invasive (BiPAP)', 'Non-Invasive (CPAP)', 'High-Flow O2', 'Weaning'];
const SOURCES = ['A&E', 'OPD', 'IPD', 'OT', 'Transfer'];

const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

function VitalChip({ label, value, unit, warn, critical }) {
    const num = parseFloat(value);
    const color = !value ? '#94a3b8' : (critical && (num <= critical[0] || num >= critical[1])) ? '#dc2626' : (warn && (num <= warn[0] || num >= warn[1])) ? '#d97706' : '#16a34a';
    return (
        <div style={{ textAlign: 'center', minWidth: 60 }}>
            <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
            <div style={{ fontWeight: 700, fontSize: 14, color }}>{value ?? '—'}{value && unit ? <span style={{ fontSize: 10, fontWeight: 400 }}>{unit}</span> : ''}</div>
        </div>
    );
}

export default function ICUCensus() {
    const navigate = useNavigate();
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdmit, setShowAdmit] = useState(false);
    const [patientSearch, setPatientSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [form, setForm] = useState({ bed_number: '', admission_source: 'A&E', admitting_doctor: '', diagnosis: '', ventilator_status: 'None' });
    const [saving, setSaving] = useState(false);
    const [doctors, setDoctors] = useState([]);

    const load = async () => {
        try {
            const res = await icuAPI.getCensus();
            setPatients(res.data.patients);
        } catch { toast.error('Failed to load ICU census'); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        api.get('/ipd/doctors').then(r => setDoctors(r.data)).catch(() => {});
    }, []);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const searchPatient = async () => {
        if (!patientSearch.trim()) return;
        try {
            const res = await patientAPI.search(patientSearch);
            setSearchResults(res.data.patients);
            if (!res.data.patients.length) toast.warning('No patient found');
        } catch { toast.error('Search failed'); }
    };

    const handleAdmit = async () => {
        if (!selectedPatient) return toast.error('Select a patient');
        if (!form.bed_number.trim()) return toast.error('Enter bed number');
        if (!form.diagnosis.trim()) return toast.error('Enter diagnosis');
        setSaving(true);
        try {
            await icuAPI.admitToICU({ patient_id: selectedPatient.id, ...form });
            toast.success('Patient admitted to ICU');
            setShowAdmit(false);
            setSelectedPatient(null);
            setPatientSearch('');
            setSearchResults([]);
            setForm({ bed_number: '', admission_source: 'A&E', admitting_doctor: '', diagnosis: '', ventilator_status: 'None' });
            load();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed');
        } finally { setSaving(false); }
    };

    const hoursIn = (dt) => {
        const h = Math.floor((Date.now() - new Date(dt)) / 3600000);
        return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d ${h % 24}h`;
    };

    const ventColor = (v) => ({ 'None': '#64748b', 'Intubated': '#dc2626', 'Non-Invasive (BiPAP)': '#d97706', 'Non-Invasive (CPAP)': '#d97706', 'High-Flow O2': '#0369a1', 'Weaning': '#059669' }[v] || '#64748b');

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 32 }}>🩺</span>
                    <div>
                        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#dc2626', margin: 0 }}>ICU / CCU</h2>
                        <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Intensive & Critical Care Unit — {patients.length} Active</p>
                    </div>
                </div>
                <button onClick={() => setShowAdmit(!showAdmit)}
                    style={{ padding: '9px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    + Admit to ICU
                </button>
            </div>

            {/* Admit Form */}
            {showAdmit && (
                <div style={{ background: 'white', borderRadius: 10, padding: 20, marginBottom: 20, border: '1px solid #fecaca' }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e3a5f', marginBottom: 16 }}>Admit Patient to ICU/CCU</h3>

                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 5 }}>Patient Search *</label>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                            <input value={patientSearch} onChange={e => setPatientSearch(e.target.value)}
                                placeholder="Search by Name / MRN / CNIC..."
                                style={{ ...inputStyle, flex: 1 }}
                                onKeyDown={e => e.key === 'Enter' && searchPatient()} />
                            <button onClick={searchPatient} style={{ padding: '8px 16px', background: '#1e3a5f', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' }}>Search</button>
                        </div>
                        {searchResults.map(p => (
                            <div key={p.id} onClick={() => { setSelectedPatient(p); setSearchResults([]); setPatientSearch(''); }}
                                style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', marginBottom: 4, fontSize: 13 }}>
                                <strong>{p.mrn}</strong> — {p.full_name} | {p.gender} | Age: {p.age_years}
                            </div>
                        ))}
                        {selectedPatient && (
                            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '8px 12px', fontSize: 13, fontWeight: 600, color: '#166534', display: 'flex', justifyContent: 'space-between' }}>
                                <span>✅ {selectedPatient.full_name} ({selectedPatient.mrn})</span>
                                <button onClick={() => setSelectedPatient(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>✕</button>
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Bed Number *</label>
                            <input style={inputStyle} value={form.bed_number} onChange={e => set('bed_number', e.target.value)} placeholder="e.g. ICU-1, CCU-3" />
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Admitted From</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={form.admission_source} onChange={e => set('admission_source', e.target.value)}>
                                {SOURCES.map(s => <option key={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Ventilator Status</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={form.ventilator_status} onChange={e => set('ventilator_status', e.target.value)}>
                                {VENT_STATUS.map(v => <option key={v}>{v}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Admitting Doctor</label>
                            <select style={{ ...inputStyle, background: 'white' }} value={form.admitting_doctor} onChange={e => set('admitting_doctor', e.target.value)}>
                                <option value="">— Select Doctor —</option>
                                {doctors.map(d => (
                                    <option key={d.id} value={d.name}>{d.name}{d.specialty ? ` — ${d.specialty}` : ''}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Diagnosis *</label>
                            <input style={inputStyle} value={form.diagnosis} onChange={e => set('diagnosis', e.target.value)} placeholder="Primary diagnosis" />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: 10 }}>
                        <button onClick={handleAdmit} disabled={saving}
                            style={{ padding: '9px 24px', background: saving ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                            {saving ? 'Admitting...' : 'Admit to ICU'}
                        </button>
                        <button onClick={() => setShowAdmit(false)} style={{ padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                    </div>
                </div>
            )}

            {/* Census */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading...</div>
            ) : patients.length === 0 ? (
                <div style={{ background: 'white', borderRadius: 10, padding: 40, textAlign: 'center', color: '#64748b', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontSize: 40, marginBottom: 10 }}>🩺</div>
                    <div style={{ fontWeight: 600 }}>ICU is empty</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Click "Admit to ICU" to add a patient</div>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {patients.map(p => (
                        <div key={p.id} onClick={() => navigate(`/icu/${p.id}`)}
                            style={{ background: 'white', borderRadius: 10, padding: '14px 18px', border: '1px solid #e2e8f0', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'}
                            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                <div style={{ width: 60, minHeight: 50, borderRadius: 10, background: '#fef2f2', border: '2px solid #fecaca', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: '4px 6px' }}>
                                    <div style={{ fontSize: 9, fontWeight: 800, color: '#dc2626', letterSpacing: 1 }}>BED</div>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: '#dc2626', textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.2, maxWidth: 52 }}>{p.bed_number}</div>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{p.full_name}</div>
                                    <div style={{ fontSize: 12, color: '#64748b' }}>{p.mrn} · {p.gender} · Age {p.age_years}</div>
                                    <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{p.diagnosis}</div>
                                </div>
                                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                                    <VitalChip label="BP" value={p.bp_systolic ? `${p.bp_systolic}/${p.bp_diastolic}` : null} warn={[[90,160],[60,100]]} />
                                    <VitalChip label="HR" value={p.heart_rate} unit="/m" warn={[[60,100]]} critical={[[40,140]]} />
                                    <VitalChip label="SpO2" value={p.spo2} unit="%" warn={[[94,100]]} critical={[[88,100]]} />
                                    <VitalChip label="Temp" value={p.temperature} unit="°C" warn={[[36.5,37.5]]} critical={[[35,39]]} />
                                    <VitalChip label="GCS" value={p.gcs} warn={[[13,15]]} critical={[[8,15]]} />
                                </div>
                                <div style={{ textAlign: 'center', minWidth: 90 }}>
                                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>VENTILATOR</div>
                                    <span style={{ background: ventColor(p.ventilator_status) + '20', color: ventColor(p.ventilator_status), borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>{p.ventilator_status}</span>
                                </div>
                                <div style={{ textAlign: 'center', minWidth: 60 }}>
                                    <div style={{ fontSize: 10, color: '#64748b', marginBottom: 4 }}>TIME IN</div>
                                    <div style={{ fontWeight: 700, fontSize: 13, color: '#374151' }}>{hoursIn(p.admitted_at)}</div>
                                </div>
                                <div style={{ color: '#94a3b8', fontSize: 18 }}>›</div>
                            </div>
                            {p.last_vitals_at && (
                                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, paddingLeft: 66 }}>
                                    Last vitals: {new Date(p.last_vitals_at).toLocaleString('en-PK')}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
